/**
 * 顶部提示条。
 *
 * 用玻璃胶囊从顶部滑入，不用系统 alert，也不引 toast 库：
 * 一句话的反馈，飘一下就够了。
 *
 * 带降级提示的错误多停 2 秒：它往往要求用户换一种操作方式，
 * 扫一眼就消失等于没提示。
 */

import {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useRef,
	useState,
	type ReactNode,
} from 'react';

import { useEnterIn } from '../motion';
import { GLYPHS, IconGlyph } from './IconGlyph';

type ToastTone = 'info' | 'success' | 'warn' | 'error';

interface ToastRecord {
	id: number;
	message: string;
	tone: ToastTone;
}

interface ToastContextValue {
	show: (message: string, tone?: ToastTone) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const TONE_STYLE: Record<ToastTone, string> = {
	info: 'text-label',
	success: 'text-ok',
	warn: 'text-warn',
	error: 'text-danger',
};

const TONE_GLYPH = {
	info: GLYPHS.info,
	success: GLYPHS.ok,
	warn: GLYPHS.warn,
	error: GLYPHS.warn,
} as const;

export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
	const [toasts, setToasts] = useState<ToastRecord[]>([]);
	const nextId = useRef(1);

	const show = useCallback((message: string, tone: ToastTone = 'info') => {
		const id = nextId.current;
		nextId.current += 1;
		setToasts((previous) => [...previous, { id, message, tone }]);
		const ttl = tone === 'error' || tone === 'warn' ? 5200 : 2600;
		window.setTimeout(() => {
			setToasts((previous) => previous.filter((toast) => toast.id !== id));
		}, ttl);
	}, []);

	const value = useMemo(() => ({ show }), [show]);

	return (
		<ToastContext.Provider value={value}>
			{children}
			<div className="pointer-events-none fixed inset-x-0 top-0 z-[60] flex flex-col items-center gap-2 px-4 pt-[max(0.75rem,env(safe-area-inset-top))]">
				{toasts.map((toast) => (
					<ToastItem key={toast.id} toast={toast} />
				))}
			</div>
		</ToastContext.Provider>
	);
}

function ToastItem({ toast }: { toast: ToastRecord }): JSX.Element {
	const ref = useEnterIn<HTMLDivElement>({ y: -14, duration: 0.34 });

	return (
		<div
			ref={ref}
			role="status"
			className="glass flex max-w-full items-start gap-2 rounded-card border border-separator px-3.5 py-2.5 shadow-e2"
		>
			<IconGlyph
				glyph={TONE_GLYPH[toast.tone]}
				size={17}
				weight="fill"
				className={`mt-px shrink-0 ${TONE_STYLE[toast.tone]}`}
			/>
			<span className="text-[13px] leading-[1.45] text-label">{toast.message}</span>
		</div>
	);
}

export function useToast(): ToastContextValue {
	const context = useContext(ToastContext);
	if (!context) throw new Error('useToast 必须在 ToastProvider 内使用');
	return context;
}
